/**
 * ExportMenu.tsx
 *
 * PLAT-10689: The Export config panel for a clip — pick languages × resolution
 * (aspect ratio is fixed to the canvas format), then Publish. PLAT-10703 made
 * it config-only: clicking Publish closes the menu INSTANTLY and the
 * persistent `PublishProgressDrawer` (top-right, driven by
 * `PublishTrackingStore`) owns all live progress UI; per-variant downloads
 * live in the Downloads tab. The dispatch continues in the background —
 * failures surface via the global toast (and as Failed rows in the drawer).
 *
 * Opened from the Clip Preview "Publish and Share" button and the Video Clips
 * card kebab, anchored to its trigger via `useAnchoredDropdown`.
 */

import { MultiSelectRow } from '@src/components/common/MultiSelectRow';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import {
  AspectRatioLogo,
  HorizontalAspectIcon,
  SquareAspectIcon,
  VerticalAspectIcon,
} from '@src/components/stage/AspectRatioUiPrimitives';
import { useEnhancementPublishGate } from '@src/hooks/useEnhancementPublishGate';
import { useStore, useTranslationStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import { AspectRatio, VideoResolution } from '@src/types.ts/video';
import {
  isOriginalLang,
  ORIGINAL_LANGUAGE,
} from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { summarizeAlreadyPublished } from '@src/utility/publishStatusSummary';
import {
  formatResolutionLabel,
  pickHighestAvailableResolutionLevel,
} from '@src/utility/videoResolution';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { useClipPublishRecords } from './hooks/useClipPublishRecords';
import { useCurrentPublishFingerprints } from './hooks/useCurrentPublishFingerprints';
import { usePublishFanout } from './hooks/usePublishFanout';

import type { MultiSelectOption } from '@src/components/common/MultiSelectRow';
import type { PendingDubRunHost } from '@src/components/content-components/hooks/ClipVideoCreatorAdapter';
import type { PublishFanoutResult } from '@src/services/publish/PublishFanoutService';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { Video } from '@src/types.ts/story';
import type { RefObject } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The Export trigger this panel hangs off (anchors below, right-aligned). */
  triggerRef: RefObject<HTMLElement>;
  /** The parent clip being published (+ its associatedVideos for AR variants). */
  mainClip: Video;
  /**
   * Re-reads the parent clip from the host's live store, for the dispatch only.
   *
   * `mainClip` above is captured when this panel last rendered, and the clip
   * preview's session store REPLACES a variant rather than editing it, so the
   * flush that runs on Publish leaves that captured object behind. A host whose
   * clip is a plain value with no store behind it omits this, and the prop is
   * used as before.
   */
  getMainClip?: () => Video;
  /** The currently viewed AR in the clip preview (pre-selected as default). */
  selectedDimension?: string;
  /**
   * Fired when the user clicks Publish (alongside onClose). Hosts with a
   * Downloads surface use it to jump there (the clip preview modal switches
   * to its Downloads tab); the Video Clips card kebab passes nothing.
   */
  onPublish?: () => void;
  /**
   * The transcript language currently selected in the host surface
   * (null/undefined = Original) — pre-selected as the default publish
   * language. Hosts without a transcript (the Video Clips card kebab) omit
   * it and keep the Original default.
   */
  defaultLanguage?: string | null;
  /**
   * The current AR's video id + clip-preview adapter. Together they let the
   * panel look up prior PublishRecords (via `useClipPublishRecords`) and
   * compute staleness (via `useCurrentPublishFingerprints`) so it can warn
   * when a selected language is already published. Omitted by hosts without a
   * Downloads surface (the Video Clips kebab / Video Selector) — the status
   * line is then suppressed. (PLAT-10882)
   */
  videoId?: string;
  /**
   * Also a `PendingDubRunHost`, so the publish can wait for a dub run the Voice
   * control still has out. The clip preview's adapter is both; the Video Clips
   * card passes neither.
   */
  adapter?: (Partial<IVideoCreatorStore> & Partial<PendingDubRunHost>) | null;

  /**
   * Jump to the host's Downloads surface (the clip preview modal switches to
   * its Downloads tab). When set, the already-published status line renders a
   * "View in Downloads" link; when omitted, no link is shown. (PLAT-10882)
   */
  onViewDownloads?: () => void;
}

const logger = LogManager.getLogger('ExportMenu');

/**
 * What a publish attempt reports back when it was refused because the dub the
 * person asked for never arrived.
 *
 * Distinct from `null`, which the fan-out already returns for "it ran and
 * failed": that gets the generic failure message, and this one gets words that
 * explain what actually happened and what to do about it.
 */
const DUB_NEVER_ARRIVED = 'dub-never-arrived';

/**
 * The panel has already closed by the time this is read, so the message has to
 * carry all of it: the dub did not arrive, nothing was published, and pressing
 * Publish again is the way forward.
 */
const DUB_NEVER_ARRIVED_MESSAGE =
  'The dub did not arrive in time. Nothing was published — please try again.';

/** Wide panel, right-aligned under the (narrow) Export trigger — Descript-style. */
const PANEL_WIDTH = 380;

const RESOLUTION_OPTIONS: {
  value: VideoResolution;
  minPx: number;
}[] = [
  { value: VideoResolution.Low, minPx: 270 },
  { value: VideoResolution.Medium, minPx: 480 },
  { value: VideoResolution.High, minPx: 720 },
  { value: VideoResolution.HD, minPx: 1080 },
  { value: VideoResolution.Original, minPx: 0 },
];

export const ExportMenu = observer(function ({
  open,
  onClose,
  triggerRef,
  mainClip,
  getMainClip,
  selectedDimension,
  onPublish,
  defaultLanguage,
  videoId,
  adapter,
  onViewDownloads,
}: Props) {
  const videoCreator = useVideoCreatorStore();
  const enhancementGate = useEnhancementPublishGate();
  const translationStore = useTranslationStore();
  const { publishTrackingStore } = useStore();

  const story = videoCreator.story;
  const originalLang = story?.originalVideoLanguage || 'en';

  // --- Config selection state ---

  const authoredLanguages = useMemo(() => {
    const codes = Array.from(translationStore.translations.keys());
    return codes.sort();
  }, [translationStore.translations.size]);

  const originalRes = story?.originalVideo
    ? Math.min(story.originalVideo.width, story.originalVideo.height)
    : undefined;
  const defaultResolution = pickHighestAvailableResolutionLevel(
    story?.originalVideo?.video,
  );

  // Default to the host's selected transcript language so publishing renders
  // what the user is looking at; fall back to Original when the selection is
  // Original or has no authored translation. Re-computed on every open — both
  // hosts mount this panel conditionally.
  const [selectedLanguages, setSelectedLanguages] = useState<Set<string>>(
    () => {
      const preferred =
        defaultLanguage &&
        !isOriginalLang(defaultLanguage) &&
        translationStore.translations.has(defaultLanguage)
          ? defaultLanguage
          : ORIGINAL_LANGUAGE;
      return new Set([preferred]);
    },
  );
  // Aspect ratio is FIXED to the format chosen on the canvas — it's shown
  // read-only here, not an export choice (rendering another ratio is a canvas
  // decision). Kept as a single-item set for the fanout inputs.
  const fixedAspectRatio = (selectedDimension ??
    mainClip.aspectRatio) as AspectRatio;
  const selectedAspectRatios = useMemo(
    () => new Set([fixedAspectRatio]),
    [fixedAspectRatio],
  );
  // Resolution is a single choice.
  const [selectedResolutions, setSelectedResolutions] = useState<
    Set<VideoResolution>
  >(() => new Set([defaultResolution]));

  const variantCount =
    selectedLanguages.size *
    selectedAspectRatios.size *
    selectedResolutions.size;

  const toggleLanguage = useCallback((lang: string) => {
    setSelectedLanguages(prev => {
      const next = new Set(prev);
      next.has(lang) ? next.delete(lang) : next.add(lang);
      return next;
    });
  }, []);

  const selectResolution = useCallback((res: VideoResolution) => {
    setSelectedResolutions(new Set([res]));
  }, []);

  const { publish } = usePublishFanout({
    // The dispatch must read the clip as it stands, not as it stood when this
    // last rendered.
    mainClip: getMainClip ?? mainClip,
    selectedLanguages,
    selectedAspectRatios,
    selectedResolutions,
    // The clip screen's own editor, when this host has one — the publish waits
    // for its save rather than the ambient Creator Studio session's, which is
    // never what Content Studio publishes (PLAT-11597). The Video Clips card
    // kebab has no live editor and passes none.
    liveEditorStore: adapter ?? null,
  });

  // Prior renders for the current AR's video (already scoped to this AR by
  // videoId) + the live staleness check — the same sources the Downloads tab
  // reads. Both hooks no-op safely when videoId/adapter are absent, so hosts
  // without a Downloads surface just get empty records.
  const { records } = useClipPublishRecords(videoId, adapter ?? null);
  const currentFingerprintFor = useCurrentPublishFingerprints(adapter ?? null);

  // Let any dub still being fetched finish, write the clip screen's live
  // timeline into the session variant, then publish.
  //
  // Switching the Voice control to Dubbed does not change the clip at once: the
  // story's dub is read over the network first, the editor command runs only
  // when that comes back, and the recipe an export reads is only rewritten a
  // second after that, by the clip screen's auto-save debounce. Publishing
  // inside that window would dispatch the recipe as it stood BEFORE the press —
  // the original speech at full level, no dubbed track, the new language's
  // captions burned over it, filed as that language's variant, and no error
  // anywhere.
  //
  // Both calls are optional because a host with no live clip editor behind it
  // has nothing to wait for and nothing to flush. The flush is additionally
  // allowed to fail without taking the publish down with it: it refuses on its
  // own to write an empty recipe over a good one, so what this catch covers is
  // an unexpected throw, after which the recipe already in the session store is
  // what publishes.
  //
  // When the wait EXPIRES the publish is refused outright and nothing at all is
  // started: dispatching would put a normal-looking row in Downloads that only
  // playing the file reveals as the wrong language.
  const flushThenPublish = useCallback(async (): Promise<
    PublishFanoutResult | null | typeof DUB_NEVER_ARRIVED
  > => {
    const waited = await adapter?.waitForPendingDubRun?.();
    if (waited === 'expired') return DUB_NEVER_ARRIVED;
    try {
      await adapter?.prepareCurrentVideoForSave?.();
    } catch (err) {
      logger.warn('Publish flush failed; publishing the stored recipe', {
        error: extractErrorInfo(err),
      });
    }
    return publish();
  }, [adapter, publish]);

  // One press, one dispatch.
  //
  // A press does not publish instantly: the wait above can hold it while a dub
  // run finishes, and the fan-out's own record round-trip follows. All the
  // person sees in that gap is "Preparing", which is exactly the state that
  // invites a second press — a second publish record, a second render billed to
  // the customer, a duplicate row in Downloads.
  //
  // The flag cannot live in this panel: both hosts mount it conditionally, so
  // the click below unmounts it and any local flag dies with it. The drawer's
  // pending count is the one piece of state that outlives the panel.
  //
  // It covers the wait and the record round-trip, not the dispatch that follows
  // the records being tracked, which stays a pre-existing open window.
  //
  // Deliberately NOT `publishTrackingStore.isPublishInProgress`, which stays
  // true for the whole render: publishing a second language while the first
  // one renders is legitimate and must keep working.
  const publishInFlight = publishTrackingStore.pendingPublishCount != null;

  // Close INSTANTLY on click — the persistent PublishProgressDrawer takes
  // over as soon as the fanout tracks its records (moments later). The
  // dispatch continues in the background; since the menu is gone, failures
  // surface via the global toast (failed-dispatch cells additionally show as
  // Failed rows in the drawer). `publish()` never rejects — it returns null
  // on total failure.
  const onPublishClick = useCallback(() => {
    onClose();
    onPublish?.();
    // Show the progress drawer this tick, before the fanout's record creates
    // round-trip — trackRecords clears this the moment the real records land.
    // Deliberately BEFORE the flush below, so the drawer never appears late.
    publishTrackingStore.beginPendingPublish(variantCount);
    void flushThenPublish().then(result => {
      if (result === DUB_NEVER_ARRIVED) {
        // Nothing was started, so the only thing to unwind is the optimistic
        // count set above — clearing it takes the drawer's "Preparing" row away
        // and re-enables Publish. The wait's own cancel has already handed the
        // Voice control back to Original, so the screen and the message agree.
        publishTrackingStore.clearPendingPublish();
        videoCreator.showToast({
          state: 'error',
          message: DUB_NEVER_ARRIVED_MESSAGE,
          delay: 6000,
        });
      } else if (!result || result.records.length === 0) {
        publishTrackingStore.clearPendingPublish();
        videoCreator.showToast({
          state: 'error',
          message: 'Publish failed — no renders were dispatched.',
          delay: 6000,
        });
      } else if (result.dispatchErrors.size > 0) {
        const failed = result.dispatchErrors.size;
        videoCreator.showToast({
          state: 'warning',
          message: `${result.records.length - failed} of ${result.records.length} renders dispatched. ${failed} failed.`,
          delay: 6000,
        });
      }
    });
  }, [
    flushThenPublish,
    onClose,
    onPublish,
    videoCreator,
    publishTrackingStore,
    variantCount,
  ]);

  // --- Compact setting-row options ---

  const languageOptions: MultiSelectOption[] = [
    {
      value: ORIGINAL_LANGUAGE,
      label: `${formatLanguageCaption(originalLang)} (Original)`,
    },
    ...authoredLanguages.map(code => ({
      value: code,
      label: formatLanguageCaption(code),
    })),
  ];
  const resolutionSelectOptions: MultiSelectOption<VideoResolution>[] =
    RESOLUTION_OPTIONS.map(opt => ({
      value: opt.value,
      label: formatResolutionLabel(opt.value),
      disabled: opt.minPx > 0 && originalRes != null && opt.minPx > originalRes,
    }));

  // Aspect-ratio glyph, matching the canvas's aspect-ratio control (the shape
  // that appears next to the ratio above the canvas — AspectRatiosDropdown).
  const AspectIcon =
    fixedAspectRatio === AspectRatio.AR_1_1
      ? SquareAspectIcon
      : fixedAspectRatio === AspectRatio.AR_9_16
        ? VerticalAspectIcon
        : HorizontalAspectIcon;

  // --- Already-published status (PLAT-10882) ---
  // Reuse the Downloads tab's data sources to warn when a selected language is
  // already published (a *current* render reads as a duplicate to avoid; an
  // outdated one as a legitimate re-publish). Called in the render path (not
  // memoized) so the MobX/observable reads stay reactive, matching DownloadsTab.
  const { publishedCount, outdatedCount, singleLabel } =
    summarizeAlreadyPublished(
      records,
      selectedLanguages,
      originalLang,
      currentFingerprintFor,
    );

  const totalSelected = selectedLanguages.size;
  let publishedStatusText = '';
  if (publishedCount > 0) {
    if (totalSelected === 1) {
      publishedStatusText =
        outdatedCount === 1
          ? 'A published version exists but is outdated — publishing will update it.'
          : `Already published in ${singleLabel}.`;
    } else {
      publishedStatusText = `${publishedCount} of ${totalSelected} selected languages are already published.`;
    }
  }

  // --- Render ---

  const { menuRef, style } = useAnchoredDropdown(
    open,
    triggerRef,
    onClose,
    undefined,
    { width: PANEL_WIDTH, align: 'end', placement: 'auto' },
  );

  if (!open || !style) return null;

  return createPortal(
    // The config panel hosts floating language/resolution dropdowns that
    // overflow its box; the panel must NOT clip them, so override the hook's
    // overflowY:auto (meant for tall scrolling menus).
    <Panel
      ref={menuRef}
      style={{ ...style, overflowY: 'visible', maxHeight: 'none' }}
    >
      <MenuHeader>
        <MenuTitle>Publish and Share</MenuTitle>
        <MenuDescription>
          Select the languages and resolution to render.
        </MenuDescription>
      </MenuHeader>

      <Settings>
        {/* Languages first, the read-only aspect-ratio fact last — choices
            first, facts last. */}
        <MultiSelectRow
          label="Languages"
          options={languageOptions}
          selected={selectedLanguages}
          onToggle={toggleLanguage}
          emptyLabel="Select a language"
          testId="export-languages"
        />
        <MultiSelectRow
          label="Resolution"
          options={resolutionSelectOptions}
          selected={selectedResolutions}
          onToggle={selectResolution}
          singleSelect
          emptyLabel="Select a resolution"
          testId="export-resolutions"
        />
        <FixedRow>
          <FixedMain>
            <FixedLabel>Aspect ratio</FixedLabel>
            <FixedValue data-testid="export-aspect-ratio">
              <AspectRatioLogo>
                <AspectIcon />
              </AspectRatioLogo>
              {fixedAspectRatio}
            </FixedValue>
          </FixedMain>
          <FixedHint>Set by the format selected on the canvas.</FixedHint>
        </FixedRow>
      </Settings>

      <VariantPreview>
        {variantCount} variant{variantCount !== 1 ? 's' : ''} will be rendered
      </VariantPreview>

      {publishedStatusText && (
        <PublishedStatus data-testid="export-published-status">
          <StatusIcon>
            <InfoIconSmall />
          </StatusIcon>
          <StatusBody>
            <span>{publishedStatusText}</span>
            {onViewDownloads && (
              <DownloadsLink
                type="button"
                data-testid="export-view-downloads"
                onClick={() => {
                  onClose();
                  onViewDownloads();
                }}
              >
                View in Downloads →
              </DownloadsLink>
            )}
          </StatusBody>
        </PublishedStatus>
      )}

      <MenuFooter>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        {/* PLAT-11227: publishing never silently ships raw audio as enhanced */}
        <span
          data-tooltip-id="export-enhancement-gate-tooltip"
          data-tooltip-content={enhancementGate.tooltip}
        >
          <PublishButton
            onClick={enhancementGate.guardPublish(onPublishClick)}
            disabled={
              variantCount === 0 || publishInFlight || enhancementGate.isBlocked
            }
          >
            {publishInFlight
              ? 'Publishing...'
              : `Publish${variantCount > 1 ? ` ${variantCount} Variants` : ''}`}
          </PublishButton>
        </span>
        {enhancementGate.isBlocked && (
          <Tooltip
            id="export-enhancement-gate-tooltip"
            style={MODAL_TOOLTIP_STYLE}
            place="top"
          />
        )}
      </MenuFooter>
    </Panel>,
    document.body,
  );
});

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

// Anchored overflow panel (positioned by useAnchoredDropdown via inline style).
// Only visuals live here — position/width come from the hook.
// Compact by design: this is an overflow menu, not a full dialog, so it uses
// its own tighter header/footer/button scale rather than the shared dialog
// primitives.
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--elevation-lg);
  padding: var(--spacing-md);
`;

const MenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
`;

const MenuTitle = styled.h2`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const MenuDescription = styled.p`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
`;

const MenuFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xs);
  padding-top: var(--spacing-2xs);
`;

const menuButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border-radius: var(--radius-input);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const CancelButton = styled.button`
  ${menuButtonBase}
  background: transparent;
  color: var(--foreground);
  border: 1px solid var(--border);
  font-weight: var(--font-weight-medium);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

const PublishButton = styled.button`
  ${menuButtonBase}
  background: var(--primary);
  color: var(--background);
  border: none;
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }
`;

// Compact settings block — one label-left / control-right row per setting:
// Languages → Resolution → the read-only Aspect ratio fact.
const Settings = styled.div`
  display: flex;
  flex-direction: column;
`;

// Fixed (read-only) setting row — mirrors MultiSelectRow's label-left / value
// chip look, but the value is static (aspect ratio is set on the canvas). The
// hint below tells the user where it comes from. Last row of the settings
// block, so no bottom border — the rows above carry the dividers.
const FixedRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const FixedMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  // Tight bottom padding so the hint below reads as part of this row.
  padding: var(--spacing-sm) 0 2px;
`;

const FixedLabel = styled.span`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  white-space: nowrap;
`;

// Read-only value: bare icon + text (no input-style box — it isn't editable,
// so it must not look like a control).
const FixedValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  // Match the dropdown pills' 8px inner padding above so this value
  // right-aligns with their content instead of the panel edge.
  padding-right: 8px;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

// Field-level helper, one step quieter than the panel subtitle (MenuDescription)
// so the two don't read as peers: smaller, tighter, sitting just under its row.
const FixedHint = styled.p`
  margin: 0;
  padding: 0 0 var(--spacing-sm);
  font-family: var(--font-primary);
  font-size: 11px;
  line-height: 1.3;
  color: var(--text-muted);
  opacity: 0.8;
`;

const VariantPreview = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

// Already-published notice as a subtle info box (below the variant count) so it
// reads as a distinct advisory, not another muted footer line. Neutral surface
// + border + info glyph, following the callout idiom (cf. BrandKit SRiskNote);
// the Downloads jump is a green (--primary) action link.
const PublishedStatus = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  padding: 10px 12px;
  border-radius: var(--radius-input);
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-secondary);
`;

// Info glyph, optically nudged to sit on the first text line.
const StatusIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--text-muted);
`;

const StatusBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
  min-width: 0;
`;

const DownloadsLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  color: var(--primary);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    color: var(--primary-hover);
    text-decoration: underline;
  }
`;
